/* =====================================================
   Damlab — Centralized Application Core
   ----------------------------------------------------
   - Single global namespace: window.App
   - Modules self-register: App.register(name, { mount })
   - mount(ctx) returns optional cleanup fn
   - All listeners use { signal } from ctx.signal
       so unmount() tears them down atomically (no leaks,
       no duplicate-listener bugs across re-inits)
   - Mount routing keyed off body[data-page]
   - Compatible with Shopify Section Rendering API
       (shopify:section:load / unload events)
   ===================================================== */
(function (root) {
  'use strict';

  if (root.App) return; // idempotent — never double-init

  // ── ReactiveStore ────────────────────────────────────
  // Shared across product / collection / cart / etc. Single
  // source of truth pattern. set(patch) merges; subscribers
  // receive (next, prev, changedKeys). Key-filtered subs:
  //   store.subscribe(['quantity'], fn)
  //   store.subscribe('*', fn)
  // Returns an unsubscribe fn from subscribe().
  class ReactiveStore {
    constructor(initial) {
      this._state = { ...(initial || {}) };
      this._subs  = new Set();
    }
    get() { return this._state; }
    set(patch) {
      const prev = this._state;
      const next = { ...prev, ...patch };
      const changed = Object.keys(patch).filter(k => prev[k] !== next[k]);
      if (!changed.length) return;
      this._state = next;
      this._subs.forEach(sub => {
        const watch = sub.keys === '*' || sub.keys.some(k => changed.includes(k));
        if (watch) {
          try { sub.fn(next, prev, changed); }
          catch (e) { console.error('[store] subscriber error', e); }
        }
      });
    }
    subscribe(keys, fn) {
      const sub = { keys, fn };
      this._subs.add(sub);
      return () => this._subs.delete(sub);
    }
  }

  // ── Tiny event bus ──────────────────────────────────
  // bus.on(event, fn, signal?) — if a signal is provided, the
  // subscription auto-unsubscribes on abort. Modules can use
  // ctx.subscribe(event, fn) inside mount() for the same effect
  // without managing the off() handle manually.
  function createBus() {
    const map = new Map();
    return {
      on(evt, fn, signal) {
        if (!map.has(evt)) map.set(evt, new Set());
        map.get(evt).add(fn);
        const off = () => map.get(evt)?.delete(fn);
        if (signal) signal.addEventListener('abort', off, { once: true });
        return off;
      },
      off(evt, fn) { map.get(evt)?.delete(fn); },
      emit(evt, payload) {
        const set = map.get(evt);
        if (!set) return;
        // copy so handlers can mutate safely
        [...set].forEach(fn => { try { fn(payload); } catch (e) { console.error(`[bus:${evt}]`, e); } });
      },
      clear() { map.clear(); },
      stats() {
        const entries = [...map.entries()].map(([evt, set]) => ({ event: evt, subscribers: set.size }));
        const total = entries.reduce((a, b) => a + b.subscribers, 0);
        return { total, events: entries };
      }
    };
  }

  const App = {
    /** Shopify-injected data + routes (set by snippets/shopify-data.liquid). */
    data: root.__shopify || {},

    /** Cross-module event bus. */
    bus: createBus(),

    /** name → { mount, unmount? } */
    _modules: new Map(),

    /** name → { ctrl, cleanup, root } — currently mounted instances */
    _mounted: new Map(),

    /** template-key → [moduleName] — populated by individual scripts */
    _routes: {},

    /** Register a module. Modules self-register at script-load time. */
    register(name, mod) {
      if (this._modules.has(name)) {
        console.warn(`[App] module already registered: ${name}`);
        return;
      }
      if (!mod || typeof mod.mount !== 'function') {
        console.error(`[App] invalid module ${name} — must have mount(ctx)`);
        return;
      }
      this._modules.set(name, mod);
    },

    /** Map a body[data-page] value to a list of module names. */
    route(pageKey, moduleNames) {
      this._routes[pageKey] = (this._routes[pageKey] || []).concat(moduleNames);
    },

    /** Mount a module (idempotent). Returns true if mounted now. */
    mount(name, options = {}) {
      if (this._mounted.has(name)) return false;
      const mod = this._modules.get(name);
      if (!mod) {
        console.warn(`[App] mount: unknown module ${name}`);
        return false;
      }
      const ctrl = new AbortController();
      const inst = { ctrl, cleanup: null, root: null, name, listenerCount: 0, mountedAt: performance.now() };
      const ctx = {
        root: options.root || document,
        signal: ctrl.signal,
        bus: this.bus,
        data: this.data,
        /** Bind a DOM listener that gets torn down on unmount. */
        on(target, evt, handler, opts) {
          target.addEventListener(evt, handler, { ...opts, signal: ctrl.signal });
          inst.listenerCount++;
        },
        /** Subscribe to a bus event for the lifetime of this mount. */
        subscribe(event, fn) {
          return App.bus.on(event, fn, ctrl.signal);
        }
      };
      inst.root = ctx.root;
      let cleanup = null;
      try {
        cleanup = mod.mount(ctx) || null;
      } catch (e) {
        console.error(`[App] mount(${name}) threw:`, e);
        ctrl.abort();
        return false;
      }
      inst.cleanup = cleanup;
      // Tracking: decrement total listener count when this instance aborts.
      ctrl.signal.addEventListener('abort', () => {
        App._listenerTotal = Math.max(0, (App._listenerTotal || 0) - inst.listenerCount);
      }, { once: true });
      App._listenerTotal = (App._listenerTotal || 0) + inst.listenerCount; // initial sync
      this._mounted.set(name, inst);
      return true;
    },

    /** Unmount: aborts all signal-bound listeners + runs cleanup fn. */
    unmount(name) {
      const inst = this._mounted.get(name);
      if (!inst) return false;
      try { inst.ctrl.abort(); } catch (_) {}
      try { inst.cleanup && inst.cleanup(); } catch (e) { console.error(`[App] cleanup(${name})`, e); }
      this._mounted.delete(name);
      return true;
    },

    /** Remount a module (used by Shopify section editor reloads). */
    remount(name, options) {
      this.unmount(name);
      return this.mount(name, options);
    },

    unmountAll() {
      [...this._mounted.keys()].forEach(n => this.unmount(n));
    },

    /** Boot: walks body[data-page] → routes → mounts. */
    boot() {
      if (this._booted) {
        console.warn('[App] boot() called twice — ignoring');
        return;
      }
      this._booted = true;
      const pageKey = document.body?.dataset.page || 'unknown';
      const list = (this._routes['*'] || []).concat(this._routes[pageKey] || []);
      // Defensive: if routes exist but no modules registered, the boot
      // probably fired before defer scripts loaded. Loud signal.
      if (list.length && this._modules.size === 0) {
        console.error(
          '[App] boot fired with', list.length, 'routed modules but 0 registered.',
          'Likely cause: ready() resolved before defer scripts ran.'
        );
      }
      list.forEach(name => this.mount(name));
      this.bus.emit('app:booted', { page: pageKey, mounted: [...this._mounted.keys()] });
    }
  };

  // Expose ReactiveStore class to other modules.
  App.ReactiveStore = ReactiveStore;

  // ── App.devtools ──────────────────────────────────────
  // Lightweight always-on instrumentation. Use during browser
  // validation + the engineering audit phase. No side effects
  // unless invoked explicitly.
  //
  //   App.devtools.report()         — full snapshot
  //   App.devtools.mounted()        — currently mounted modules
  //   App.devtools.registered()     — all registered module names
  //   App.devtools.listeners()      — total DOM listeners attached via ctx.on()
  //   App.devtools.busStats()       — bus subscriber counts per event
  //   App.devtools.heap()           — Chrome perf.memory snapshot
  //   App.devtools.cartConsistency()— UI ↔ App.cart.state coherence check
  //   App.devtools.stateSnapshot()  — pulled state across all reactive stores
  //
  // Future-add slots noted: observer registry (during audit phase),
  // forced-reflow detector (PerformanceObserver), bus profiling.
  App.devtools = {
    mounted() {
      return [...App._mounted.entries()].map(([name, inst]) => ({
        name,
        listeners: inst.listenerCount,
        ageMs: Math.round(performance.now() - inst.mountedAt),
        root: inst.root === document ? 'document' : (inst.root?.tagName || 'fragment')
      }));
    },
    registered() { return [...App._modules.keys()].sort(); },
    listeners() { return App._listenerTotal || 0; },
    busStats() { return App.bus.stats(); },
    heap() {
      const m = performance.memory;
      if (!m) return 'performance.memory unavailable (Chrome only)';
      const mb = b => Math.round(b / 1048576) + ' MB';
      return { used: mb(m.usedJSHeapSize), total: mb(m.totalJSHeapSize), limit: mb(m.jsHeapSizeLimit) };
    },
    cartConsistency() {
      const state = App.cart?.state;
      if (!state) return { ok: false, reason: 'no cart state' };
      const drawer = document.querySelector('[data-cart-drawer]');
      if (!drawer) return { ok: false, reason: 'no drawer mounted' };
      const renderedKeys = [...drawer.querySelectorAll('[data-line-key]')].map(n => n.dataset.lineKey).filter(Boolean);
      const stateKeys = (state.items || []).map(i => i.key);
      const orphaned = renderedKeys.filter(k => !stateKeys.includes(k));
      const missing  = stateKeys.filter(k => !renderedKeys.includes(k));
      const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
      const renderedSubtotal = subtotalEl?.textContent || '';
      return {
        ok: !orphaned.length && !missing.length,
        rendered: renderedKeys.length,
        state: stateKeys.length,
        orphaned, missing,
        subtotal: { rendered: renderedSubtotal, stateCents: state.items_subtotal_price ?? state.total_price ?? 0 },
        itemCount: state.item_count ?? 0
      };
    },
    stateSnapshot() {
      return {
        cart:       App.cart?.state ? { item_count: App.cart.state.item_count, items: App.cart.state.items?.length } : null,
        product:    App.product?.state ? {
          variant: App.product.state.selectedVariant?.title,
          quantity: App.product.state.quantity,
          activeMediaIndex: App.product.state.activeMediaIndex,
          sellingPlanId: App.product.state.sellingPlanId
        } : null,
        collection: App.collection?.state ? {
          totalProducts: App.collection.state.allProducts.length,
          visibleCount: App.collection.state.visibleIds.size,
          filters: { ...App.collection.state.filters, use: [...App.collection.state.filters.use] },
          sort: App.collection.state.sort
        } : null
      };
    },
    report() {
      console.group('%cDamlab DevTools Report', 'color:#0077B6;font-weight:bold');
      console.log('Mounted modules:'); console.table(App.devtools.mounted());
      console.log('Tracked DOM listeners:', App.devtools.listeners());
      console.log('Bus:', App.devtools.busStats());
      console.log('Heap:', App.devtools.heap());
      console.log('Cart consistency:', App.devtools.cartConsistency());
      console.log('State:', App.devtools.stateSnapshot());
      console.groupEnd();
    }
  };

  root.App = App;

  // ── Boot lifecycle ──────────────────────────────────
  // IMPORTANT: with <script defer>, this file executes during
  // readyState === 'interactive' — AFTER parse, BEFORE the other
  // defer scripts (animations.js, chrome.js, cart.js, etc.) have
  // run, and BEFORE DOMContentLoaded fires. If we boot now, the
  // module registry is empty and nothing mounts. So we must wait
  // for DOMContentLoaded UNLESS it has already fired (readyState
  // === 'complete'). 'loading' and 'interactive' both mean
  // "DCL has not fired yet" → schedule.
  function ready(fn) {
    if (document.readyState === 'complete') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    }
  }
  ready(() => App.boot());

  // ── Shopify Section Rendering API (future-proof) ────
  // When the merchant edits a section in the theme editor, Shopify
  // re-renders just that section's HTML. We need to remount any
  // module whose root sits inside that section.
  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = e.target;
    const moduleName = sectionEl?.dataset.module;
    if (moduleName) App.remount(moduleName, { root: sectionEl });
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const moduleName = e.target?.dataset.module;
    if (moduleName) App.unmount(moduleName);
  });

})(window);
